import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './dialogs.css';

const openShells: symbol[] = [];

export function DialogShell({ eyebrow, title, onClose, children, className }: { eyebrow: string; title: string; onClose: () => void; children: ReactNode; className?: string }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const id = Symbol('dialog');
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    openShells.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openShells[openShells.length - 1] !== id) return;
      e.stopPropagation();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      const at = openShells.indexOf(id);
      if (at >= 0) openShells.splice(at, 1);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <div className="p1-mailing">
      <div className="campaign-type-backdrop" onMouseDown={onClose}>
        <div className={`card shadow campaign-type-dialog marketing-type-dialog mailing-dialog${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
          <div className="campaign-type-head"><div><span>{eyebrow}</span><h2>{title}</h2></div><button type="button" className="btn-close" aria-label="Zavřít" onClick={onClose} /></div>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

export interface NoticeOptions {
  title: string;
  message?: ReactNode;
}

export interface PromptOptions {
  title: string;
  label?: string;
  message?: ReactNode;
  initial?: string;
  placeholder?: string;
  confirmLabel?: string;
  readOnly?: boolean;
}

type DialogRequest =
  | { id: number; kind: 'confirm'; options: ConfirmOptions; resolve: (v: boolean) => void }
  | { id: number; kind: 'notice'; options: NoticeOptions; resolve: () => void }
  | { id: number; kind: 'prompt'; options: PromptOptions; resolve: (v: string | null) => void };

type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;
type NewRequest = WithoutId<DialogRequest>;

let queue: DialogRequest[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function enqueue(request: NewRequest) {
  queue = [...queue, { ...request, id: nextId++ } as DialogRequest];
  emit();
}

function dequeue(id: number) {
  queue = queue.filter((r) => r.id !== id);
  emit();
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => enqueue({ kind: 'confirm', options, resolve }));
}

export function noticeDialog(options: NoticeOptions): Promise<void> {
  return new Promise((resolve) => enqueue({ kind: 'notice', options, resolve }));
}

export function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => enqueue({ kind: 'prompt', options, resolve }));
}

export async function insertLinkDialog(): Promise<void> {
  const selection = window.getSelection();
  const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
  const url = (await promptDialog({ title: 'Vložit odkaz', label: 'Adresa odkazu', initial: 'https://', placeholder: 'https://…', confirmLabel: 'Vložit odkaz' }))?.trim();
  if (!url || url === 'https://') return;
  if (range) {
    const node = range.commonAncestorContainer;
    const host = (node instanceof Element ? node : node.parentElement)?.closest<HTMLElement>('[contenteditable="true"]');
    host?.focus({ preventScroll: true });
    const current = window.getSelection();
    current?.removeAllRanges();
    current?.addRange(range);
  }
  document.execCommand('createLink', false, url);
}

function PromptBody({ options, onDone }: { options: PromptOptions; onDone: (v: string | null) => void }) {
  const [value, setValue] = useState(options.initial ?? '');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); input.current?.select(); }, []);
  return (
    <form className="mailing-dialog-body" onSubmit={(e) => { e.preventDefault(); onDone(options.readOnly ? null : value); }}>
      {options.message && <p className="p1-dialog-text">{options.message}</p>}
      <label className="mailing-dialog-field">
        {options.label && <span>{options.label}</span>}
        <input ref={input} className="form-control" value={value} readOnly={options.readOnly} placeholder={options.placeholder} onChange={(e) => setValue(e.target.value)} />
      </label>
      <div className="mailing-dialog-actions">
        {options.readOnly
          ? <button type="submit" className="btn btn-sm btn-primary">Zavřít</button>
          : (
            <>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onDone(null)}>Zrušit</button>
              <button type="submit" className="btn btn-sm btn-primary">{options.confirmLabel ?? 'Uložit'}</button>
            </>
          )}
      </div>
    </form>
  );
}

export function DialogHost() {
  const current = useSyncExternalStore(subscribe, () => queue[0] ?? null);
  if (!current) return null;
  if (current.kind === 'confirm') {
    const done = (v: boolean) => { dequeue(current.id); current.resolve(v); };
    const { title, message, confirmLabel, danger } = current.options;
    return (
      <DialogShell key={current.id} eyebrow="Potvrzení" title={title} onClose={() => done(false)}>
        <div className="mailing-dialog-body">
          {message && <p className="p1-dialog-text">{message}</p>}
          <div className="mailing-dialog-actions">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => done(false)}>Zrušit</button>
            <button type="button" className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`} autoFocus onClick={() => done(true)}>{confirmLabel ?? 'Potvrdit'}</button>
          </div>
        </div>
      </DialogShell>
    );
  }
  if (current.kind === 'notice') {
    const done = () => { dequeue(current.id); current.resolve(); };
    return (
      <DialogShell key={current.id} eyebrow="Upozornění" title={current.options.title} onClose={done}>
        <div className="mailing-dialog-body">
          {current.options.message && <p className="p1-dialog-text">{current.options.message}</p>}
          <div className="mailing-dialog-actions">
            <button type="button" className="btn btn-sm btn-primary" autoFocus onClick={done}>Rozumím</button>
          </div>
        </div>
      </DialogShell>
    );
  }
  const done = (v: string | null) => { dequeue(current.id); current.resolve(v); };
  return (
    <DialogShell key={current.id} eyebrow={current.options.readOnly ? 'Odkaz' : 'Zadání'} title={current.options.title} onClose={() => done(null)}>
      <PromptBody options={current.options} onDone={done} />
    </DialogShell>
  );
}
